import React, { useState, useEffect } from 'react';
import { Calendar } from 'lucide-react';
import { ServiceFacilityContent, facilityService } from '../services/facility';
import Navbar from '../component/includes/navbar';
import Footer from '../component/includes/footer';

const FacilityDetail: React.FC = () => {
  const [facilities, setFacilities] = useState<ServiceFacilityContent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [imageError, setImageError] = useState<Record<number, boolean>>({});

  useEffect(() => {
    const fetchFacilities = async () => {
      try {
        const fetchedFacilities = await facilityService.getFacilityContent();
        setFacilities(fetchedFacilities);
        setLoading(false);
      } catch (err) {
        setError('Gagal memuat fasilitas');
        setLoading(false);
      }
    };

    fetchFacilities();
  }, []);

  const handleImageError = (facilityId: number) => {
    setImageError(prev => ({
      ...prev,
      [facilityId]: true
    }));
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center py-10 text-lg font-semibold">Memuat...</div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-red-500 text-center py-10 text-lg">{error}</div>
        </div>
      );
    }

    if (facilities.length === 0) {
      return (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center py-10 text-lg font-semibold">
            Tidak ada fasilitas tersedia saat ini
          </div>
        </div>
      );
    }

    return (
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="space-y-12 mt-14">
          {facilities.map((facility) => (
            <article 
              key={facility.id} 
              className="bg-white shadow-lg rounded-xl overflow-hidden"
            >
              {facility.image && !imageError[facility.id] && (
                <div className="w-full">
                  <div className="relative pt-[56.25%]">
                    <img 
                      src={`${process.env.REACT_APP_API_URL}${facility.image}`} 
                      alt={facility.title}
                      onError={() => handleImageError(facility.id)}
                      className="absolute inset-0 w-full h-full object-contain bg-gray-100"
                      loading="lazy"
                    />
                  </div>
                </div>
              )}
              <div className="p-8 lg:p-10">
                <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
                  {facility.title}
                </h2>
                <div className="flex items-center text-gray-600 text-sm mb-6">
                  <Calendar size={18} className="mr-2" />
                  <time dateTime={facility.created_at}>
                    {new Date(facility.created_at).toLocaleDateString('id-ID', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                    })}
                  </time>
                </div>
                <div 
                  className="prose prose-lg max-w-none text-gray-700"
                  dangerouslySetInnerHTML={{ __html: facility.description }}
                />
              </div>
            </article>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <Navbar />
      <main className="flex-grow py-12">
        {renderContent()}
      </main>
      <Footer />
    </div>
  );
};

export default FacilityDetail;